import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { useDmReadingDetail } from "@/api/districts/district-queries";
import { useReadingDetail } from "@/api/reseller/queries";
import type { MqttMeterReading } from "@/api/reseller/types";
import { ReadingDetailInfoCards } from "@/components/mqtt/reading-detail-info-cards";
import { ReadingStatusBadge } from "@/components/mqtt/reading-status-badge";
import { BackButton } from "@/components/navigation/back-button";
import { CopyableCell } from "@/components/shared/copyable-cell";
import type { ColumnDef } from "@/components/table";
import { DataTable } from "@/components/table";
import { Badge } from "@/components/ui/badge";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { isValidLang, ROUTE_SLUGS } from "@/config/routes";
import { useAuth } from "@/contexts/auth-context";
import { useDebounce } from "@/hooks/use-debounce";
import { getUserRole } from "@/types/auth";

const DEFAULT_PAGE_SIZE = 100;

export function ReadingDetailPage() {
	const { t, i18n } = useTranslation();
	const { id, lang } = useParams<{ id: string; lang: string }>();
	const navigate = useNavigate();
	const currentLang = lang && isValidLang(lang) ? lang : "pl";
	const { user } = useAuth();
	const role = user ? getUserRole(user) : null;
	const isDistrictManager = role === "district_manager";
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [search, setSearch] = useState("");
	const [ordering, setOrdering] = useState("");
	const [statusFilter, setStatusFilter] = useState("");
	const debouncedSearch = useDebounce(search);
	const queryParams = {
		page,
		page_size: pageSize,
		status: statusFilter || undefined,
		search: debouncedSearch || undefined,
		ordering: ordering || undefined,
	};
	const dmQuery = useDmReadingDetail(Number(id), queryParams, {
		enabled: role === "district_manager",
	});
	const resellerQuery = useReadingDetail(Number(id), queryParams, {
		enabled: role !== null && role !== "district_manager",
	});
	const { data, isLoading } = isDistrictManager ? dmQuery : resellerQuery;

	const statusSelect = (
		<Select
			value={statusFilter || "__all__"}
			onValueChange={(v) => {
				setStatusFilter(v === "__all__" ? "" : v);
				setPage(1);
			}}
		>
			<SelectTrigger className="w-44">
				<SelectValue placeholder={t("mqttReadingsTable.colStatus")} />
			</SelectTrigger>
			<SelectContent>
				<SelectItem value="__all__">{t("dataTable.all")}</SelectItem>
				<SelectItem value="completed">
					{t("readingDetail.statusCompleted")}
				</SelectItem>
				<SelectItem value="error">{t("readingDetail.statusError")}</SelectItem>
				<SelectItem value="no_communication">
					{t("readingDetail.statusNoCommunication")}
				</SelectItem>
				<SelectItem value="request_sent">
					{t("readingDetail.statusRequestSent")}
				</SelectItem>
			</SelectContent>
		</Select>
	);

	const meterReadingsData = data?.meter_readings;

	const columns: ColumnDef<MqttMeterReading>[] = [
		{
			key: "meter",
			label: t("readingDetail.colSerialNumber"),
			sortField: "meter__serial_number",
			render: (row) => (
				<CopyableCell
					value={row.meter.serial_number}
					label={t("readingDetail.colSerialNumber")}
				/>
			),
		},
		{
			key: "status",
			label: t("mqttReadingsTable.colStatus"),
			sortField: "status",
			render: (row) => <ReadingStatusBadge status={row.status} />,
		},
		{
			key: "last_communication",
			label: t("mqttMeterReadings.colLastCommunication"),
			sortField: "meter__last_communication",
			render: (row) =>
				row.last_communication ? (
					new Date(row.last_communication).toLocaleString(i18n.language, {
						dateStyle: "short",
						timeStyle: "short",
					})
				) : (
					<span className="text-muted-foreground">—</span>
				),
		},
	];

	function goBack() {
		navigate(`/${currentLang}/${ROUTE_SLUGS.readings[currentLang]}`);
	}

	return (
		<div className="flex min-h-0 flex-1 flex-col gap-4 p-4 lg:p-6">
			<div className="flex flex-wrap items-center gap-3">
				<BackButton onClick={goBack} />
				<h1 className="min-w-0 text-lg font-semibold">
					{isLoading ? (
						<Skeleton className="inline-block h-6 w-40" />
					) : (
						t("readingDetail.title")
					)}
				</h1>
				{data && (
					<>
						<Badge
							variant={data.completed ? "success" : "info"}
							className="shrink-0 text-xs"
						>
							{data.completed
								? t("mqttReadingsTable.statusCompleted")
								: t("mqttReadingsTable.statusInProgress")}
						</Badge>
						<span className="text-xs text-muted-foreground">
							{t("readingDetail.readingDate")}:{" "}
							{new Date(data.reading_date).toLocaleString(i18n.language, {
								dateStyle: "short",
								timeStyle: "short",
							})}
						</span>
					</>
				)}
			</div>

			<ReadingDetailInfoCards data={data} isLoading={isLoading} />

			<div className="flex min-h-0 flex-1 flex-col gap-2">
				<h3 className="text-base font-semibold">
					{t("readingDetail.metersTitle", {
						count: data?.meter_readings_count ?? 0,
					})}
				</h3>
				<DataTable
					data={meterReadingsData}
					columns={columns}
					loading={isLoading}
					page={page}
					pageSize={pageSize}
					onPageChange={setPage}
					onPageSizeChange={(size) => {
						setPageSize(size);
						setPage(1);
					}}
					search={search}
					onSearchChange={(v) => {
						setSearch(v);
						setPage(1);
					}}
					searchPlaceholder={t("readingDetail.searchPlaceholder")}
					ordering={ordering}
					onOrderingChange={(o) => {
						setOrdering(o);
						setPage(1);
					}}
					headerActions={statusSelect}
				/>
			</div>
		</div>
	);
}
